import type { ReferenceOption } from "@fieldpath/shared";
import { useState } from "react";

interface Props {
  label: string;
  people: ReferenceOption[];
  selected: string[];
  onChange: (keys: string[]) => void;
  hint?: string;
}

/**
 * Choosing one or more people. Scrollable rather than a native multi-select,
 * which on Windows requires holding Ctrl and quietly loses the whole selection
 * if you forget.
 *
 * A hundred names is too many to scroll past, so there is a filter. Anyone
 * already chosen stays visible regardless of what is typed — filtering
 * someone out of sight while they remain selected is how people accidentally
 * assign work to someone they cannot see.
 */
export function PeoplePicker({ label, people, selected, onChange, hint }: Props) {
  const [filter, setFilter] = useState("");

  const toggle = (key: string) =>
    onChange(
      selected.includes(key) ? selected.filter((k) => k !== key) : [...selected, key],
    );

  const needle = filter.trim().toLowerCase();
  const shown = needle
    ? people.filter(
        (p) => p.label.toLowerCase().includes(needle) || selected.includes(p.key),
      )
    : people;

  return (
    <div className="field">
      <label>{label}</label>
      {hint ? (
        <p className="muted" style={{ margin: "0 0 6px" }}>
          {hint}
        </p>
      ) : null}

      {/* Only worth the space once the list is long enough to be a nuisance. */}
      {people.length > 8 ? (
        <input
          type="search"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder={`Filter ${people.length} people…`}
          style={{ marginBottom: 6 }}
        />
      ) : null}

      <div
        style={{
          maxHeight: 170,
          overflowY: "auto",
          border: "1px solid var(--line)",
          borderRadius: 6,
          padding: "8px 10px",
          background: "var(--card)",
        }}
      >
        {people.length === 0 ? (
          <span className="muted">No one available</span>
        ) : shown.length === 0 ? (
          <span className="muted">Nobody matches “{filter}”</span>
        ) : (
          shown.map((p) => (
            <label
              key={p.key}
              style={{
                display: "flex",
                gap: 8,
                alignItems: "center",
                marginBottom: 4,
                color: "var(--ink)",
                fontSize: 14,
              }}
            >
              <input
                type="checkbox"
                style={{ width: "auto" }}
                checked={selected.includes(p.key)}
                onChange={() => toggle(p.key)}
              />
              {p.label}
            </label>
          ))
        )}
      </div>

      <p className="muted" style={{ marginTop: 4, marginBottom: 0 }}>
        {selected.length === 0 ? "Nobody selected" : `${selected.length} selected`}
        {needle && shown.length < people.length
          ? ` · showing ${shown.length} of ${people.length}`
          : ""}
      </p>
    </div>
  );
}
